import type { FabricObject } from 'fabric';
import { ObjectTypeEnum } from '../../_enums/object-type.enum';
import { isMnemonicEllipse, normalizeMnemonicEllipseDimensions } from './mnemonic-ellipse.util';

export const MNEMONIC_PRIMITIVE_STROKE_WIDTH = 2;

export const MNEMONIC_PRIMITIVE_DEFAULTS = {
    strokeWidth: MNEMONIC_PRIMITIVE_STROKE_WIDTH,
    strokeUniform: true,
} as const;

const PRIMITIVE_OBJECT_TYPES = new Set<string>([ObjectTypeEnum.rect, ObjectTypeEnum.ellipse, ObjectTypeEnum.line]);
const PRIMITIVE_FABRIC_TYPES = new Set(['rect', 'ellipse', 'line']);
const UNIFORM_SCALE_OBJECT_TYPES = new Set<string>([
    ObjectTypeEnum.chart,
    ObjectTypeEnum.gauge,
    ObjectTypeEnum.image,
    ObjectTypeEnum.defaultImage,
]);

const BAKE_UNIFORM_SCALE_OBJECT_TYPES = new Set<string>([ObjectTypeEnum.chart, ObjectTypeEnum.gauge]);

export function isMnemonicPrimitive(object: unknown): boolean {
    const typedObject = object as { type?: string; objectType?: string } | null | undefined;

    if (!typedObject) {
        return false;
    }

    return PRIMITIVE_OBJECT_TYPES.has(typedObject.objectType ?? '') || PRIMITIVE_FABRIC_TYPES.has(typedObject.type ?? '');
}

export function isUniformScaleOnlyObject(object: unknown): boolean {
    const typedObject = object as { objectType?: string } | null | undefined;

    return UNIFORM_SCALE_OBJECT_TYPES.has(typedObject?.objectType ?? '');
}

export function isMnemonicImageObject(object: unknown): boolean {
    const typedObject = object as { objectType?: string; type?: string } | null | undefined;

    return (
        typedObject?.objectType === ObjectTypeEnum.image ||
        typedObject?.objectType === ObjectTypeEnum.defaultImage ||
        typedObject?.type === 'image'
    );
}

export function shouldBakeUniformAspectScale(object: unknown): boolean {
    const typedObject = object as { objectType?: string } | null | undefined;

    return BAKE_UNIFORM_SCALE_OBJECT_TYPES.has(typedObject?.objectType ?? '');
}

export function applyMnemonicPrimitiveStrokeDefaults(object: { strokeWidth?: number; strokeUniform?: boolean; set?: (key: string, value: unknown) => unknown }): void {
    if (!isMnemonicPrimitive(object)) {
        return;
    }

    if (object.strokeWidth == null) {
        object.strokeWidth = MNEMONIC_PRIMITIVE_DEFAULTS.strokeWidth;
    }

    object.strokeUniform = true;
}

export function applyMnemonicArrowStrokeDefaults(group: { getObjects?: () => Array<{ type?: string; strokeUniform?: boolean }> }): void {
    group.getObjects?.().forEach((child) => {
        if (child.type === 'line') {
            child.strokeUniform = true;
        }
    });
}

export function restrictToUniformScalingControls(object: FabricObject): void {
    object.setControlsVisibility({
        mt: false,
        mb: false,
        ml: false,
        mr: false,
    });
}

export function applyMnemonicObjectBehavior(object: FabricObject): void {
    const typedObject = object as FabricObject & { objectType?: string; getObjects?: () => FabricObject[] };

    applyMnemonicPrimitiveStrokeDefaults(typedObject);

    if (typedObject.objectType === ObjectTypeEnum.group) {
        applyMnemonicArrowStrokeDefaults(typedObject);
    }

    if (isUniformScaleOnlyObject(typedObject)) {
        restrictToUniformScalingControls(typedObject);
    }

    normalizeMnemonicImageScale(typedObject);
}

export function enforceUniformScaling(object: FabricObject): void {
    const scaleX = object.scaleX ?? 1;
    const scaleY = object.scaleY ?? 1;

    if (Math.abs(Math.abs(scaleX) - Math.abs(scaleY)) < 0.0001) {
        return;
    }

    const scale = Math.max(Math.abs(scaleX), Math.abs(scaleY));

    object.set({
        scaleX: scale * (scaleX < 0 ? -1 : 1),
        scaleY: scale * (scaleY < 0 ? -1 : 1),
    });
}

export function bakeMnemonicPrimitiveScale(object: FabricObject): boolean {
    if (!isMnemonicPrimitive(object)) {
        return false;
    }

    const scaleX = object.scaleX ?? 1;
    const scaleY = object.scaleY ?? 1;

    if (Math.abs(scaleX - 1) < 0.0001 && Math.abs(scaleY - 1) < 0.0001) {
        return false;
    }

    const typedObject = object as FabricObject & {
        type?: string;
        objectType?: string;
        width?: number;
        height?: number;
        rx?: number;
        ry?: number;
    };

    if (typedObject.type === 'rect' || typedObject.objectType === ObjectTypeEnum.rect) {
        object.set({
            width: (typedObject.width ?? 0) * Math.abs(scaleX),
            height: (typedObject.height ?? 0) * Math.abs(scaleY),
            scaleX: scaleX < 0 ? -1 : 1,
            scaleY: scaleY < 0 ? -1 : 1,
        });
        return true;
    }

    if (typedObject.type === 'ellipse' || isMnemonicEllipse(typedObject)) {
        object.set({
            rx: (typedObject.rx ?? 0) * Math.abs(scaleX),
            ry: (typedObject.ry ?? 0) * Math.abs(scaleY),
            scaleX: scaleX < 0 ? -1 : 1,
            scaleY: scaleY < 0 ? -1 : 1,
        });
        normalizeMnemonicEllipseDimensions(typedObject);
        return true;
    }

    if (typedObject.type === 'line' || typedObject.objectType === ObjectTypeEnum.line) {
        object.set({
            width: (typedObject.width ?? 0) * Math.abs(scaleX),
            height: (typedObject.height ?? 0) * Math.abs(scaleY),
            scaleX: scaleX < 0 ? -1 : 1,
            scaleY: scaleY < 0 ? -1 : 1,
        });
        return true;
    }

    return false;
}

export function bakeUniformAspectScale(object: FabricObject): boolean {
    if (!shouldBakeUniformAspectScale(object)) {
        return false;
    }

    const scaleX = object.scaleX ?? 1;
    const scaleY = object.scaleY ?? 1;

    if (Math.abs(scaleX - 1) < 0.0001 && Math.abs(scaleY - 1) < 0.0001) {
        return false;
    }

    enforceUniformScaling(object);

    object.set({
        width: object.getScaledWidth(),
        height: object.getScaledHeight(),
        scaleX: 1,
        scaleY: 1,
    });

    return true;
}

export function buildMnemonicImageSizeUpdate(
    object: FabricObject,
    dimension: 'width' | 'height',
    value: number,
): Record<string, number> | null {
    const image = object as FabricObject & { getOriginalSize?: () => { width: number; height: number } };

    if (typeof image.getOriginalSize !== 'function') {
        return null;
    }

    const natural = image.getOriginalSize();
    const naturalWidth = natural.width || image.width || 1;
    const naturalHeight = natural.height || image.height || 1;
    const scale = dimension === 'width' ? value / naturalWidth : value / naturalHeight;

    return {
        width: naturalWidth,
        height: naturalHeight,
        scaleX: scale,
        scaleY: scale,
    };
}

export function normalizeMnemonicImageScale(object: FabricObject): void {
    if (!isMnemonicImageObject(object)) {
        return;
    }

    const image = object as FabricObject & { getOriginalSize?: () => { width: number; height: number } };

    if (typeof image.getOriginalSize !== 'function') {
        return;
    }

    const natural = image.getOriginalSize();
    const naturalWidth = natural.width || image.width || 0;
    const naturalHeight = natural.height || image.height || 0;

    if (!naturalWidth || !naturalHeight) {
        return;
    }

    const scaleX = image.scaleX ?? 1;
    const scaleY = image.scaleY ?? 1;
    const displayWidth = image.width * Math.abs(scaleX);
    const displayHeight = image.height * Math.abs(scaleY);
    const widthWasBaked = Math.abs(scaleX - 1) < 0.0001 && Math.abs(scaleY - 1) < 0.0001 && (image.width > naturalWidth * 1.01 || image.height > naturalHeight * 1.01);

    if (widthWasBaked) {
        const scale = Math.max(displayWidth / naturalWidth, displayHeight / naturalHeight);

        image.set({
            width: naturalWidth,
            height: naturalHeight,
            scaleX: scale,
            scaleY: scale,
        });
        enforceUniformScaling(image);
        return;
    }

    if (Math.abs(image.width - naturalWidth) > 1 || Math.abs(image.height - naturalHeight) > 1) {
        const scale = Math.max(displayWidth / naturalWidth, displayHeight / naturalHeight);

        image.set({
            width: naturalWidth,
            height: naturalHeight,
            scaleX: scale * Math.sign(scaleX || 1),
            scaleY: scale * Math.sign(scaleY || 1),
        });
        enforceUniformScaling(image);
    }
}
